import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Modal } from "../components/ui-bits";
import { useBrandPermissions } from "../lib/brand";
import { fmtDate } from "../components/sampling/common";
import { SlidersHorizontal } from "lucide-react";

const MOVE = { carico: "Carico produzione", vendita: "Ordine", rettifica: "Rettifica", annullo: "Ordine annullato" };

function AdjustModal({ onClose, onDone }) {
  const [garments, setGarments] = useState([]);
  const [f, setF] = useState({ garment_id: "", size: "", color: "", qty: "", note: "" });
  const [err, setErr] = useState("");
  useEffect(() => { api.get("/garments").then(r => setGarments(r.data)).catch(() => {}); }, []);
  const g = garments.find(x => x.id === f.garment_id);
  const save = async () => {
    setErr("");
    try { await api.post("/finished-stock/adjust", { ...f, qty: Number(f.qty) }); onDone(); }
    catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Modal open onClose={onClose} title="Rettifica giacenza">
      <div className="space-y-4" data-testid="fs-adjust-modal">
        <select className="input-dark" value={f.garment_id} onChange={e => setF({ ...f, garment_id: e.target.value })} data-testid="fs-adjust-garment">
          <option value="">Scegli capo…</option>{garments.map(x => <option key={x.id} value={x.id}>{x.code} — {x.name}</option>)}</select>
        <div className="grid grid-cols-3 gap-3">
          <input list="fs-sizes" placeholder="Taglia" className="input-dark" value={f.size} onChange={e => setF({ ...f, size: e.target.value })} data-testid="fs-adjust-size"/>
          <input list="fs-colors" placeholder="Colore" className="input-dark" value={f.color} onChange={e => setF({ ...f, color: e.target.value })} data-testid="fs-adjust-color"/>
          <input type="number" placeholder="+/- pezzi" className="input-dark" value={f.qty} onChange={e => setF({ ...f, qty: e.target.value })} data-testid="fs-adjust-qty"/>
        </div>
        <datalist id="fs-sizes">{(g?.sizes || []).map(s => <option key={s} value={s}/>)}</datalist>
        <datalist id="fs-colors">{(g?.colors || []).map(s => <option key={s} value={s}/>)}</datalist>
        <input placeholder="Motivo (es. inventario, reso)" className="input-dark" value={f.note} onChange={e => setF({ ...f, note: e.target.value })} data-testid="fs-adjust-note"/>
        {err && <div className="text-sm text-red-600" data-testid="fs-adjust-error">{err}</div>}
        <div className="flex justify-end"><button disabled={!f.garment_id || !Number(f.qty)} onClick={save} className="btn-gold" data-testid="fs-adjust-save">Salva</button></div>
      </div>
    </Modal>
  );
}

function Moves({ garmentId }) {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/finished-stock/moves", { params: { garment_id: garmentId || undefined } }).then(r => setItems(r.data)).catch(() => setItems([])); }, [garmentId]);
  if (!items) return null;
  return (
    <div className="card-dark overflow-x-auto mt-8"><div className="label-caps p-4 pb-0">Movimenti recenti</div>
      <table className="tbl w-full text-sm" data-testid="fs-moves">
        <thead><tr><th>Data</th><th>Capo</th><th>Taglia / Colore</th><th>Tipo</th><th>Riferimento</th><th className="text-right">Pezzi</th><th className="text-right">Saldo</th></tr></thead>
        <tbody>{items.length === 0 ? <tr><td colSpan={7} className="opacity-60">Nessun movimento.</td></tr> : items.map(m => (
          <tr key={m.id} data-testid={`fs-move-${m.id}`}><td>{fmtDate(m.at)}</td><td>{m.garment_code}</td><td>{m.size || "—"} / {m.color || "—"}</td>
            <td>{MOVE[m.type]}</td><td>{m.ref || m.note || "—"}</td>
            <td className={`text-right ${m.qty < 0 ? "text-red-600" : "text-green-700"}`}>{m.qty > 0 ? `+${m.qty}` : m.qty}</td><td className="text-right">{m.balance}</td></tr>))}</tbody>
      </table></div>
  );
}

export default function FinishedStockPage() {
  const { canEdit } = useBrandPermissions();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [onlyShort, setOnlyShort] = useState(false);
  const [adjust, setAdjust] = useState(false);
  const [garment, setGarment] = useState("");
  const [tick, setTick] = useState(0);
  const load = () => { api.get("/finished-stock").then(r => setData(r.data)).catch(() => setData({ rows: [] })); setTick(t => t + 1); };
  useEffect(() => { load(); }, []);
  const rows = useMemo(() => (data?.rows || []).filter(r => (!onlyShort || r.qty < 0) && (!garment || r.garment_id === garment)
    && `${r.garment_code} ${r.garment_name} ${r.size} ${r.color}`.toLowerCase().includes(q.toLowerCase())), [data, q, onlyShort, garment]);
  const garments = useMemo(() => [...new Map((data?.rows || []).map(r => [r.garment_id, r])).values()], [data]);
  if (!data) return <div className="text-sm opacity-60">Caricamento…</div>;
  return (
    <div data-testid="finished-stock-page">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="card-dark p-5"><div className="label-caps mb-2">Capi disponibili</div><div className="h-editorial text-3xl" data-testid="fs-total">{data.total_pieces}</div></div>
        <div className="card-dark p-5"><div className="label-caps mb-2">Modelli a magazzino</div><div className="h-editorial text-3xl" data-testid="fs-garments">{data.garments}</div></div>
        <div className="card-dark p-5"><div className="label-caps mb-2">Da produrre</div><div className="h-editorial text-3xl" style={data.to_produce ? { color: "#dc2626" } : {}} data-testid="fs-to-produce">{data.to_produce}</div></div>
      </div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <input placeholder="Cerca capo, taglia, colore" className="input-dark !w-64" value={q} onChange={e => setQ(e.target.value)} data-testid="fs-search"/>
        <select className="input-dark !w-auto" value={garment} onChange={e => setGarment(e.target.value)} data-testid="fs-filter-garment">
          <option value="">Tutti i capi</option>{garments.map(g => <option key={g.garment_id} value={g.garment_id}>{g.garment_code}</option>)}</select>
        <label className="text-sm inline-flex items-center gap-2"><input type="checkbox" checked={onlyShort} onChange={e => setOnlyShort(e.target.checked)} data-testid="fs-only-short"/> Solo da produrre</label>
        {canEdit && <button onClick={() => setAdjust(true)} className="btn-ghost ml-auto inline-flex items-center gap-2" data-testid="fs-adjust-btn"><SlidersHorizontal size={14}/> Rettifica</button>}
      </div>
      {rows.length === 0 ? <div className="text-sm opacity-60 p-10 border border-dashed text-center" data-testid="fs-empty">Nessun capo a magazzino. I capi si caricano quando confermi la consegna di una produzione.</div> : (
        <div className="card-dark overflow-x-auto"><table className="tbl w-full" data-testid="fs-table">
          <thead><tr><th>Capo</th><th>Taglia</th><th>Colore</th><th className="text-right">Giacenza</th><th>Stato</th></tr></thead>
          <tbody>{rows.map(r => (
            <tr key={r.id} data-testid={`fs-row-${r.id}`}><td>{r.garment_code} <span className="opacity-60">{r.garment_name}</span></td><td>{r.size || "—"}</td><td>{r.color || "—"}</td>
              <td className="text-right font-medium" data-testid={`fs-qty-${r.id}`}>{r.qty}</td>
              <td>{r.qty < 0 ? <span className="text-red-600 text-sm">Da produrre {-r.qty}</span> : r.qty === 0 ? <span className="opacity-60 text-sm">Esaurito</span> : <span className="text-green-700 text-sm">Disponibile</span>}</td></tr>))}</tbody>
        </table></div>)}
      <Moves key={tick} garmentId={garment}/>
      {adjust && <AdjustModal onClose={() => setAdjust(false)} onDone={() => { setAdjust(false); load(); }}/>}
    </div>
  );
}
